import { Layout } from "@/components/Layout";
import { TeamCard } from "@/components/Cards";
import { teamMembers } from "@/data/index";
import { IMAGES } from "@/assets/images";
import { motion } from "framer-motion";
import { springPresets, staggerContainer, staggerItem } from "@/lib/motion";
import { Users, Award, Globe } from "lucide-react";

export default function Leadership() {
  return (
    <Layout>
      <div className="relative">
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.TEAM_HERO_10}
              alt="Leadership Team"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <div className="container mx-auto px-4 relative z-10 text-center py-24">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                Leadership Team
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
                Meet the visionaries driving AI-led innovation across Canada
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-b from-background to-muted/20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                60+ Years of Combined Excellence
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our leadership team brings together decades of expertise in technology, business development, and operational excellence
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {[
                {
                  icon: <Users className="w-8 h-8" />,
                  title: "60+ Years",
                  description: "Combined Leadership Experience",
                },
                {
                  icon: <Award className="w-8 h-8" />,
                  title: "Global Expertise",
                  description: "Working with Fortune 500 Companies",
                },
                {
                  icon: <Globe className="w-8 h-8" />,
                  title: "Canadian Presence",
                  description: "Serving Businesses Across Ontario",
                },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                  className="bg-card rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow"
                  style={{
                    boxShadow: "0 8px 30px -6px color-mix(in srgb, var(--primary) 15%, transparent)",
                  }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4">
                    {stat.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{stat.title}</h3>
                  <p className="text-muted-foreground">{stat.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {teamMembers.map((member) => (
                <motion.div key={member.id} variants={staggerItem}>
                  <TeamCard member={member} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.BUSINESS_MEETING_7}
              alt="Team Collaboration"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/80 to-background/70" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Building the Future Together
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Our leadership team is committed to delivering world-class AI-led IT services that transform businesses and drive sustainable growth. With expertise spanning sales, technology, construction and marine engineering technologies, and global system integration, we bring a unique perspective to every client engagement.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-3">Strategic Vision</h3>
                  <p className="text-muted-foreground">
                    Combining decades of industry experience with cutting-edge AI innovation to chart the course for digital transformation
                  </p>
                </div>
                <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-3">Operational Excellence</h3>
                  <p className="text-muted-foreground">
                    Leveraging global best practices and proven methodologies to ensure flawless execution and client success
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
